import { useMemo, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { LwTable, LwSortableHeader, LwStatusPill } from '@/components/lw';
import {
  computeCurrentConcessionSummary, computeRosterTotals, planBasisLabelForUser,
  type CommissionEarning, type ReportUser, type RosterRow
} from './myCommissionReportModel';
import { commissionMoney as money, commissionWholeMoney } from './commissionPresentation';

type SortKey = 'name' | 'record_count' | 'earned' | 'paid' | 'owed' | 'concessions' | 'profitabilityBonuses' | 'loProfitability';

export function CommissionRosterTable({ roster, onSelect, earnings = [], users = [], trend = null }: {
  roster: RosterRow[];
  onSelect: (userId: string) => void;
  earnings?: CommissionEarning[];
  users?: ReportUser[];
  trend?: Map<string, number> | null;
}) {
  const [sort, setSort] = useState<{ key: SortKey; descending: boolean }>({ key: 'earned', descending: true });
  const rows = useMemo(() => roster.map(row => ({
    ...row,
    concessions: computeCurrentConcessionSummary(earnings.filter(earning => earning.lo_user_id === row.user_id)).concessionsTotal,
    basis: planBasisLabelForUser(users.find(user => user.user_id === row.user_id) ?? null).primary
  })), [roster, earnings, users]);
  const sorted = rows.map((row, index) => ({ ...row, index })).sort((a, b) => {
    const cmp = sort.key === 'name' ? (a.name ?? '').localeCompare(b.name ?? '') : (a[sort.key] ?? 0) - (b[sort.key] ?? 0);
    return (sort.descending ? -cmp : cmp) || a.index - b.index;
  });
  const totals = computeRosterTotals(roster);
  const columns: [SortKey, string][] = [['name', 'User'], ['record_count', 'Records'], ['earned', 'Earned'], ['paid', 'Paid'],
  ['owed', 'Owed'], ['concessions', 'Concessions'], ['profitabilityBonuses', 'Bonuses'], ['loProfitability', 'LO Profitability']];
  return <section className="mc-roster" aria-label="Commission users">
    <div className="mc-roster-heading"><h2>Commission users</h2><span>{roster.length} users · {totals.recordCount} records</span></div>
    <LwTable presentation="canonical" density="compact" ariaLabel="Commission users" scrollRegionLabel="Commission user table">
      <thead><tr>{columns.map(([key, label]) => <LwSortableHeader key={key} label={label} align={key === 'name' ? 'left' : 'right'}
        direction={sort.key === key ? sort.descending ? 'descending' : 'ascending' : false}
        onSort={() => setSort(previous => ({ key, descending: previous.key === key ? !previous.descending : key !== 'name' }))} />)}<th scope="col"><span className="sr-only">View user</span></th></tr></thead>
      <tbody>{sorted.length === 0 ? <tr><td colSpan={9} className="mc-empty">No commission users in this period.</td></tr>
        : sorted.map(row => {
          const share = totals.earned > 0 ? row.earned / totals.earned * 100 : 0;
          const change = trend?.get(row.user_id);
          return <tr key={row.user_id} onClick={() => onSelect(row.user_id)}>
            <td><button type="button" className="mc-user-link" onClick={event => {
              event.stopPropagation(); onSelect(row.user_id);
            }}><span className="font-medium">{row.name ?? '—'}</span></button>
              {row.employment_status !== null && row.employment_status !== 'active' && <LwStatusPill label={row.employment_status} tone="neutral" />}
              <small>{row.basis}</small></td>
            <td className="is-right">{row.record_count}</td>
            <td className="is-right"><strong>{money(row.earned)}</strong><div className="mc-share"><span style={{ width: `${Math.max(0, Math.min(100, share))}%` }} /></div><small>{share.toFixed(1)}% of company</small></td>
            <td className="is-right mc-positive">{money(row.paid)}</td>
            <td className={`is-right${row.owed > 0.01 ? ' mc-warning' : ''}`}>{money(row.owed)}</td>
            <td className="is-right mc-warning">{row.concessions ? money(row.concessions) : '—'}</td>
            <td className="is-right">{row.profitabilityBonuses ? money(row.profitabilityBonuses) : '—'}</td>
            <td className={`is-right ${row.loProfitability < 0 ? 'mc-negative' : 'mc-positive'}`}><strong>{money(row.loProfitability)}</strong>
              {change !== undefined && <small className={`mc-trend ${change < 0 ? 'mc-negative' : change > 0 ? 'mc-positive' : 'mc-muted'}`}>
                {change === 0 ? 'No change' : `${change > 0 ? '▴' : '▾'} ${commissionWholeMoney(Math.abs(change))}`} vs prior period
              </small>}
            </td>
            <td><ChevronRight aria-hidden="true" /></td>
          </tr>;
        })}</tbody>
      {sorted.length > 0 && <tfoot><tr><th scope="row">Total ({roster.length} users)</th><td className="is-right">{totals.recordCount}</td>
        <td className="is-right">{money(totals.earned)}</td><td className="is-right">{money(totals.paid)}</td><td className="is-right">{money(totals.owed)}</td>
        <td className="is-right">{money(rows.reduce((sum, row) => sum + row.concessions, 0))}</td><td className="is-right">{money(rows.reduce((sum, row) => sum + (row.profitabilityBonuses ?? 0), 0))}</td>
        <td className="is-right">{money(totals.loProfitability)}</td><td /></tr></tfoot>}
    </LwTable>
  </section>;
}

export default CommissionRosterTable;
